import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import QRScanModal from '../../components/QRScanModal';
import { errorText } from '../../utils/errorText';
import { badge, box, button, input, label } from '../shared/styles';
import MicroscopyUpload from './MicroscopyUpload';
import MicroscopyViewer from './MicroscopyViewer';
import PlateConditionsFields, { conditionsPayload } from '../../components/PlateConditionsFields';
import * as scanQueue from '../../utils/scanQueue';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { intakeNav, specimenNav } from '../../utils/linkedRecordNav';

/*
 * Scan station (Task 6.9): scan anything with a label -- a specimen, plate,
 * slant, tube, vessel, instrument, experiment or unit -- see what it is, and
 * do the next thing to it.
 *
 * A handheld barcode scanner types the code and presses Enter, so the input
 * keeps focus after every action (as Lab Mode does); the camera button decodes
 * the same text with the webcam. The server says which actions the person may
 * take; each is an existing API path.
 */
const LINKABLE = { collection: 'sample', external: 'external', plate: 'plate', slant: 'slant', strain: 'strain',
  experiment: 'experiment', experiment_unit: 'experiment_unit' };
const PLATE_STATUSES = ['pending', 'ready_to_transfer', 'slow_growth', 'contaminated', 'in_rescue', 'failed'];
//: routes/plate_checks.py: ready_to_transfer needs where the culture goes next.
const TRANSFER_TO = ['plate', 'slant'];
//: routes/iso.py log_maintenance.
const MAINTENANCE_KINDS = ['calibration', 'preventive', 'verification', 'repair'];
const MAINTENANCE_RESULTS = ['pass', 'fail', 'adjusted', 'done'];
//: What a scanned code can be the source of a transfer into a vessel as (routes/scan_station.py).
const TRANSFER_KINDS = { vessel: 'vessel', plate: 'plate', slant: 'slant', strain: 'strain' };

//: Where each kind's own page opens it (utils/linkedRecordNav.js's state keys),
//: from the scan's id and details. A specimen the reader sees only redacted
//: opens in the hub's dossier instead (specimenNav), and an intake in the
//: hub's intake drawer (intakeNav).
const RECORD_PAGE = {
  collection: (id) => ({ path: '/collections', state: { openCollectionId: id } }),
  external: (id) => intakeNav(id),
  plate: (id) => ({ path: '/plates', state: { openPlateId: id } }),
  slant: (id) => ({ path: '/slants', state: { openSlantId: id } }),
  dna: (id) => ({ path: '/dna', state: { openDnaId: id } }),
  dna_run: (id) => ({ path: '/dna', state: { openRunId: id } }),
  specimen: (id, details) => specimenNav({ ...details, id }),
  strain: () => ({ path: '/strains' }),
  experiment: () => ({ path: '/experiments' }),
  experiment_unit: () => ({ path: '/experiments' }),
};

//: Recently scanned, kept on this device per person: a per-viewer convenience.
const RECENT_LIMIT = 12;
const recentKey = (userId) => `mb_scan_recent:${userId || 'anon'}`;
export function readRecent(userId) {
  try {
    const list = JSON.parse(localStorage.getItem(recentKey(userId)) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}
function writeRecent(userId, list) {
  try { localStorage.setItem(recentKey(userId), JSON.stringify(list)); } catch { /* storage off: this visit only */ }
}

function showValue(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'boolean') return v ? '✓' : '✗';
  if (Array.isArray(v)) {
    return v.length ? v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ') : null;
  }
  if (typeof v === 'object') {
    const parts = Object.entries(v).filter(([, x]) => x !== null && x !== '')
      .map(([k, x]) => `${k}: ${typeof x === 'object' ? JSON.stringify(x) : x}`);
    return parts.length ? parts.join(' · ') : null;
  }
  return String(v);
}

//: Fields the detail view edits in place, per kind, and the route that takes them.
const EDITABLE = {
  plate: { path: (item) => `/plates/${item.id}`, fields: ['notes', 'temperature_c'] },
};

/* The whole record behind a scan: every detail the server sent, the fields
 * that can be corrected here, the actions that append to it, and a way to its
 * own page for everything else. */
function ScanDetail({ item, t, onClose, onSaved, children }) {
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const edit = EDITABLE[item.kind];
  const [draft, setDraft] = useState(() => Object.fromEntries((edit?.fields || []).map((f) => [f, item.details?.[f] ?? ''])));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    dialogRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const page = RECORD_PAGE[item.kind]?.(item.id, item.details);
  const rows = Object.entries(item.details || {}).map(([k, v]) => [k, showValue(v)]).filter(([, v]) => v !== null);
  const save = async (e) => {
    e.preventDefault();
    const body = {};
    for (const f of edit.fields) {
      const before = item.details?.[f] ?? '';
      if (String(draft[f] ?? '') === String(before)) continue;
      body[f] = f === 'temperature_c' ? (draft[f] === '' ? null : Number(draft[f])) : (draft[f] || null);
    }
    if (!Object.keys(body).length) return;
    setSaving(true);
    setError('');
    try {
      await api.put(edit.path(item), body);
      onSaved(t('detail.saved'));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="scan-detail-overlay">
      <div role="dialog" aria-modal="true" aria-labelledby="scan-detail-title" tabIndex={-1} ref={dialogRef}
        style={dialog} data-testid="scan-detail">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <h3 id="scan-detail-title" style={{ margin: 0 }}>
            <span style={badge('primary')}>{t(`kind_${item.kind}`, { defaultValue: item.kind })}</span>{' '}
            {item.code} {item.title && `· ${item.title}`}
          </h3>
          <button type="button" onClick={onClose} aria-label={t('detail.close')} title={t('detail.close')}
            data-testid="scan-detail-close">✕</button>
        </div>
        {item.status && (
          <p style={{ margin: '6px 0' }}><span style={badge('info')}>{t(`opt_${item.status}`, { defaultValue: item.status })}</span></p>
        )}
        {page && (
          <button type="button" style={button('var(--c-info)')} title={t('tip.openRecord')}
            onClick={() => navigate(page.path, page.state ? { state: page.state } : undefined)}
            data-testid="scan-detail-open">{t('detail.openRecord')}</button>
        )}
        <h4>{t('detail.info')}</h4>
        <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, auto) 1fr', gap: '4px 12px', fontSize: 13, margin: 0 }}>
          {rows.map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt style={{ fontWeight: 600 }}>{t(`detail_${k}`, { defaultValue: k })}</dt>
              <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{v}</dd>
            </div>
          ))}
        </dl>
        {edit && (
          <form onSubmit={save} style={{ ...box, marginTop: 12 }} data-testid="scan-detail-edit">
            <strong>{t('detail.edit')}</strong>
            {edit.fields.map((f) => (
              <label key={f} style={label}>{t(`detail_${f}`, { defaultValue: f })}
                {f === 'notes'
                  ? <textarea rows={3} style={input} value={draft[f] ?? ''} data-testid={`scan-edit-${f}`}
                      onChange={(e) => setDraft({ ...draft, [f]: e.target.value })} />
                  : <input style={input} inputMode="decimal" value={draft[f] ?? ''} data-testid={`scan-edit-${f}`}
                      onChange={(e) => setDraft({ ...draft, [f]: e.target.value })} />}
              </label>
            ))}
            <button type="submit" disabled={saving} style={button('var(--c-primary)')} data-testid="scan-detail-save">
              {saving ? t('detail.saving') : t('save')}</button>
            {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
          </form>
        )}
        {children && <><h4>{t('detail.append')}</h4>{children}</>}
      </div>
    </div>
  );
}
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'flex-start',
  justifyContent: 'center', padding: 16, zIndex: 1000, overflowY: 'auto' };
const dialog = { background: 'var(--c-surface)', color: 'var(--c-text-fg)', borderRadius: 10, padding: 20, width: 'min(720px, 100%)',
  boxShadow: '0 10px 30px rgba(0,0,0,.3)', outline: 'none' };
const recentCard = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '8px 12px', minWidth: 140,
  borderRadius: 8, border: '1px solid var(--c-border)', background: 'var(--c-surface)', color: 'var(--c-text-fg)', cursor: 'pointer',
  textAlign: 'start', flex: '0 0 auto' };

function ActionForm({ action, item, onDone, onQueued, t }) {
  const [values, setValues] = useState({});
  const [error, setError] = useState('');
  const set = (k) => (e) => setValues({ ...values, [k]: e.target.value });
  const plansFor = (kind) => (action.plans || []).filter((p) => p.kind === kind);
  const bodyOf = () => {
    switch (action.action) {
      case 'plate_check':
        return { status: values.status || 'pending', colony_diameter_mm: values.value ? Number(values.value) : null,
          notes: values.notes || null, ...conditionsPayload(values),
          ...(values.status === 'ready_to_transfer' ? { transfer_to: values.transfer_to || TRANSFER_TO[0] } : {}) };
      case 'reading':
        return { parameter: values.parameter || action.parameters[0], value: Number(values.value) };
      case 'observe':
        return { observations: [{ unit_id: action.unit_id, outcome: values.outcome || action.outcomes[0],
          value: Number(values.value), ...(values.timepoint ? { timepoint: values.timepoint } : {}) }] };
      case 'set_status':
        return { status: values.status || (action.statuses || [])[1] || 'out_of_service', reason: values.reason };
      case 'raise_nc':
        return { title: values.title, description: values.notes || values.title, severity: values.severity || 'minor',
          source: 'observation', instrument_id: action.instrument_id };
      case 'log_maintenance': {
        const kind = values.kind || MAINTENANCE_KINDS[0];
        const plan = values.plan || (plansFor(kind)[0] || {}).plan_id;
        return { kind, result: values.result || MAINTENANCE_RESULTS[0],
          performed_on: new Date().toISOString().slice(0, 10), performed_by: values.by || 'lab',
          reason: values.reason, ...(plan && plan !== 'none' ? { plan_id: plan } : {}) };
      }
      case 'transfer_in':
        return { volume_ml: values.volume_ml ? Number(values.volume_ml) : null, note: values.note || null };
      default:
        return {};
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    let body = bodyOf();
    if (action.action === 'transfer_in') {
      // The source is scanned too: its label says what it is.
      try {
        const { data: source } = await api.get(`/scan/${encodeURIComponent(String(values.source || '').trim())}`);
        if (!TRANSFER_KINDS[source.kind]) { setError(t('transfer_source_unknown')); return; }
        body = { ...body, from: { kind: TRANSFER_KINDS[source.kind], id: source.id } };
      } catch (err) {
        setError(err?.response?.status === 404 ? t('transfer_source_unknown') : errorText(err));
        return;
      }
    }
    // Every write carries a key, so the offline queue can replay it safely.
    const write = { path: action.path, body, key: scanQueue.newKey(),
      label: `${t(`action_${action.action}`)} · ${item.code}` };
    try {
      await scanQueue.send(api, write);
      setValues({});
      onDone(t(`done_${action.action}`));
    } catch (err) {
      if (scanQueue.unreachable(err) && scanQueue.add(write)) {
        setValues({});
        onQueued(t('queued'));
      } else {
        setError(errorText(err));
      }
    }
  };
  const field = (k, props = {}) => (
    <label style={label} key={k}>{t(`field_${k}`)}
      <input style={input} value={values[k] || ''} onChange={set(k)} data-testid={`${action.action}-${k}`}
        title={{ value: t('tip.field_value'), notes: t('tip.field_notes'), timepoint: t('tip.field_timepoint'),
          reason: t('tip.field_reason'), title: t('tip.field_title'), by: t('tip.field_by'), source: t('tip.field_source'),
          volume_ml: t('tip.field_volume_ml'), note: t('tip.field_note') }[k]} {...props} />
    </label>
  );
  const select = (k, options) => (
    <label style={label} key={k}>{t(`field_${k}`)}
      <select style={input} value={values[k] || options[0]} onChange={set(k)} data-testid={`${action.action}-${k}`}
        title={{ status: t('tip.field_status'), transfer_to: t('tip.field_transfer_to'), parameter: t('tip.field_parameter'),
          outcome: t('tip.field_outcome'), severity: t('tip.field_severity'), kind: t('tip.field_kind'),
          result: t('tip.field_result') }[k]}>
        {options.map((o) => <option key={o} value={o}>{t(`opt_${o}`, { defaultValue: o })}</option>)}
      </select>
    </label>
  );
  const fields = {
    plate_check: [select('status', PLATE_STATUSES),
      ...(values.status === 'ready_to_transfer' ? [select('transfer_to', TRANSFER_TO)] : []),
      field('value', { inputMode: 'decimal' }),
      <PlateConditionsFields key="conditions" values={values} onChange={setValues} labelStyle={label} inputStyle={input} />,
      field('notes')],
    reading: [select('parameter', action.parameters || []), field('value', { inputMode: 'decimal' })],
    observe: [select('outcome', action.outcomes || []), field('value', { inputMode: 'decimal' }),
      ...(action.needs_timepoint ? [field('timepoint')] : [])],
    set_status: [select('status', item.kind === 'instrument'
      ? ['in_service', 'out_of_service', 'quarantined', 'retired'] : (action.statuses || []).filter((s) => s !== item.status)),
    field('reason')],
    raise_nc: [field('title'), field('notes'), select('severity', ['minor', 'major', 'critical'])],
    log_maintenance: [select('kind', MAINTENANCE_KINDS), select('result', MAINTENANCE_RESULTS),
      ...(plansFor(values.kind || MAINTENANCE_KINDS[0]).length
        ? [<label style={label} key="plan">{t('field_plan')}
          <select style={input} value={values.plan || plansFor(values.kind || MAINTENANCE_KINDS[0])[0].plan_id}
            onChange={set('plan')} title={t('tip.field_plan')} data-testid="log_maintenance-plan">
            {plansFor(values.kind || MAINTENANCE_KINDS[0]).map((p) => (
              <option key={p.plan_id} value={p.plan_id}>{`${p.description} · ${p.next_due}`}</option>))}
            <option value="none">{t('opt_no_plan')}</option>
          </select></label>] : []),
      field('by'), field('reason')],
    transfer_in: [field('source'), field('volume_ml', { inputMode: 'decimal' }), field('note')],
  }[action.action] || [];
  return (
    <form onSubmit={submit} style={{ ...box, marginBottom: 8 }} data-testid={`action-${action.action}`}>
      <strong>{t(`action_${action.action}`)}</strong>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {fields}
        <button type="submit" style={button('var(--c-primary)')} data-testid={`action-${action.action}-go`}
          title={{ plate_check: t('tip.save_plate_check'), reading: t('tip.save_reading'), observe: t('tip.save_observe'),
            set_status: t('tip.save_set_status'), raise_nc: t('tip.save_raise_nc'),
            log_maintenance: t('tip.save_log_maintenance'), transfer_in: t('tip.save_transfer_in') }[action.action]}>{t('save')}</button>
      </div>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
    </form>
  );
}

export default function ScanStation() {
  const { t } = useTranslation('scan');
  const userId = useAuth()?.user?.user_id;
  const [code, setCode] = useState('');
  const [item, setItem] = useState(null);
  const [images, setImages] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [recent, setRecent] = useState(() => readRecent(userId));
  const [detailOpen, setDetailOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [queued, setQueued] = useState(() => scanQueue.list());
  const [lineage, setLineage] = useState(null);
  const inputRef = useRef(null);

  // Replay what could not be sent: on opening the station, when the browser
  // says the network is back, and on request. Stable across renders (t is
  // read through a ref), so the effect below runs once, not on every render.
  const tRef = useRef(t);
  tRef.current = t;
  const flushQueue = useCallback(async () => {
    if (!scanQueue.list().length) return;
    const result = await scanQueue.flush(api);
    setQueued(scanQueue.list());
    if (result.sent) setMessage(tRef.current('queue_sent', { count: result.sent }));
  }, []);
  useEffect(() => {
    flushQueue();
    window.addEventListener('online', flushQueue);
    return () => window.removeEventListener('online', flushQueue);
  }, [flushQueue]);

  // Back to the scan input after every action -- unless someone is typing in a form field.
  useEffect(() => {
    const active = document.activeElement;
    if (!scanning && (!active || active === document.body)) inputRef.current?.focus();
  });

  const loadLineage = useCallback(async (found) => {
    if (found.kind !== 'vessel') { setLineage(null); return; }
    try {
      setLineage((await api.get(`/vessels/${found.id}/lineage`)).data);
    } catch {
      setLineage(null);
    }
  }, []);

  const loadImages = useCallback(async (found) => {
    const kind = LINKABLE[found.kind];
    if (!kind) { setImages([]); return; }
    try {
      setImages((await api.get('/microscopy', { params: { kind, id: found.id } })).data);
    } catch {
      setImages([]);
    }
  }, []);

  const resolve = useCallback(async (raw, openDetail = false) => {
    const value = String(raw || '').trim();
    if (!value) return;
    setDetailOpen(false);
    setError('');
    setMessage('');
    setViewing(null);
    try {
      const { data } = await api.get(`/scan/${encodeURIComponent(value)}`);
      setItem(data);
      setRecent((list) => {
        const entry = { code: data.code || value, kind: data.kind, title: data.title || null, status: data.status || null,
          at: new Date().toISOString() };
        const next = [entry, ...list.filter((r) => r.code !== entry.code)].slice(0, RECENT_LIMIT);
        writeRecent(userId, next);
        return next;
      });
      await loadImages(data);
      await loadLineage(data);
      if (openDetail) setDetailOpen(true);
    } catch (e) {
      setItem(null);
      setImages([]);
      setLineage(null);
      setError(e?.response?.status === 404 ? t('notFound', { code: value }) : errorText(e));
    }
    setCode('');
  }, [loadImages, loadLineage, t, userId]);

  const refresh = async (note) => {
    setMessage(note);
    if (item) {
      const { data } = await api.get(`/scan/${encodeURIComponent(item.code)}`);
      setItem(data);
      await loadLineage(data);
    }
  };
  const closeDetail = useCallback(() => setDetailOpen(false), []);
  const queuedNote = (note) => { setMessage(note); setQueued(scanQueue.list()); };
  const actions = (item?.actions || []).filter((a) => a.action !== 'attach_microscopy');
  const attach = (item?.actions || []).find((a) => a.action === 'attach_microscopy');

  return (
    <div>
      <h2>{t('pageTitle')}</h2>
      <p style={{ color: 'var(--c-text-muted-fg)', marginTop: 0 }}>{t('intro')}</p>
      <form onSubmit={(e) => { e.preventDefault(); resolve(code); }} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input ref={inputRef} style={{ ...input, flex: 1, fontSize: 18 }} value={code} placeholder={t('placeholder')}
          onChange={(e) => setCode(e.target.value)} title={t('tip.scanInput')} data-testid="scan-input" aria-label={t('placeholder')} autoComplete="off" />
        <button type="submit" style={button('var(--c-primary)')} title={t('tip.find')} data-testid="scan-go">{t('find')}</button>
        <button type="button" onClick={() => setScanning(true)} title={t('tip.camera')} data-testid="scan-camera">{t('camera')}</button>
      </form>
      {recent.length > 0 && (
        <section aria-labelledby="scan-recent-title" style={{ marginBottom: 12 }} data-testid="scan-recent">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h3 id="scan-recent-title" style={{ fontSize: 15, margin: '4px 0' }}>{t('recentTitle')}</h3>
            <button type="button" onClick={() => { setRecent([]); writeRecent(userId, []); }} title={t('tip.recentClear')}
              style={{ fontSize: 12 }} data-testid="scan-recent-clear">{t('recentClear')}</button>
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
            {recent.map((r) => (
              <button key={r.code} type="button" onClick={() => resolve(r.code, true)} title={t('tip.recent')}
                style={recentCard} data-testid={`scan-recent-${r.code}`}>
                <span style={{ fontSize: 11, color: 'var(--c-text-muted-fg)' }}>{t(`kind_${r.kind}`, { defaultValue: r.kind })}</span>
                <strong>{r.code}</strong>
                {r.title && r.title !== r.code && <span style={{ fontSize: 12 }}>{r.title}</span>}
                {r.at && <span style={{ fontSize: 11, color: 'var(--c-text-muted-fg)' }}>{new Date(r.at).toLocaleString()}</span>}
              </button>
            ))}
          </div>
        </section>
      )}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }} data-testid="scan-error">{error}</p>}
      {message && <p style={{ color: 'var(--c-success-fg)' }} data-testid="scan-message">{message}</p>}
      {queued.length > 0 && (
        <section style={{ ...box, borderColor: 'var(--c-warning-fg)' }} data-testid="scan-queue">
          <strong>{t('queue_title', { count: queued.length })}</strong>
          <button type="button" onClick={flushQueue} title={t('tip.queueSend')} style={{ marginInlineStart: 8 }} data-testid="scan-queue-send">
            {t('queue_send')}</button>
          <ul style={{ margin: '6px 0 0', fontSize: 13 }}>
            {queued.map((q) => (
              <li key={q.key} data-testid={`queued-${q.key}`}>
                {q.label} · {new Date(q.queuedAt).toLocaleTimeString()}
                {q.failed && (
                  <>
                    {' '}<span style={{ color: 'var(--c-danger-fg)' }}>{t('queue_failed')}: {q.failed}</span>{' '}
                    <button type="button" onClick={() => { scanQueue.dismiss(q.key); setQueued(scanQueue.list()); }}
                      title={t('tip.queueDismiss')}>
                      {t('queue_dismiss')}</button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {item && (
        <section style={box} data-testid="scan-result">
          <h3 style={{ marginTop: 0, cursor: 'pointer' }} onClick={() => setDetailOpen(true)} title={t('tip.openDetail')}>
            <span style={badge('primary')} data-testid="scan-kind">{t(`kind_${item.kind}`, { defaultValue: item.kind })}</span>{' '}
            {item.code} {item.title && `· ${item.title}`}
            {item.status && <span style={badge('info')} data-testid="scan-status">{t(`opt_${item.status}`, { defaultValue: item.status })}</span>}
          </h3>
          <button type="button" onClick={() => setDetailOpen(true)} style={{ ...button('var(--c-info)'), marginBottom: 8 }}
            title={t('tip.openDetail')} data-testid="scan-open-detail">{t('detail.open')}</button>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px', fontSize: 13 }}>
            {Object.entries(item.details || {}).filter(([, v]) => v !== null && v !== '' && typeof v !== 'object')
              .slice(0, 12).map(([k, v]) => (
                <div key={k} style={{ display: 'contents' }}><dt style={{ fontWeight: 600 }}>{t(`detail_${k}`, { defaultValue: k })}</dt>
                  <dd style={{ margin: 0 }}>{String(v)}</dd></div>
              ))}
          </dl>
          {actions.length === 0 && <p style={{ fontSize: 13 }}>{t('noActions')}</p>}
          {actions.map((a) => <ActionForm key={a.action} action={a} item={item} t={t} onDone={refresh} onQueued={queuedNote} />)}

          {lineage && (lineage.ancestors.length > 0 || lineage.descendants.length > 0) && (
            <div data-testid="vessel-lineage" style={{ fontSize: 13, margin: '8px 0' }}>
              {lineage.ancestors.length > 0 && (
                <p style={{ margin: '2px 0' }}><strong>{t('lineage_from')}:</strong>{' '}
                  {lineage.ancestors.map((a) => `${a.code || a.id} (${t(`kind_${a.kind}`, { defaultValue: a.kind })}`
                    + `${a.depth > 1 ? `, ${t('lineage_depth', { depth: a.depth })}` : ''}, ${t(`lineage_via_${a.via}`)})`).join(' · ')}</p>
              )}
              {lineage.descendants.length > 0 && (
                <p style={{ margin: '2px 0' }}><strong>{t('lineage_into')}:</strong>{' '}
                  {lineage.descendants.map((d) => d.code).join(' · ')}</p>
              )}
            </div>
          )}

          <h4>{t('microscopy')} ({images.length})</h4>
          {attach && <MicroscopyUpload link={attach.link} onDone={(img) => { setImages([img, ...images]); setViewing(img); }} />}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {images.map((img) => (
              <button key={img.image_id} type="button" onClick={() => setViewing(img)} title={t('tip.thumb')} data-testid={`thumb-${img.image_id}`}
                style={{ border: '1px solid var(--c-border)', padding: 2, background: 'none' }}>
                {img.thumbnail_url
                  ? <img src={resolvePhotoUrl(img.thumbnail_url)} alt={img.objective || t('image')} loading="lazy"
                      style={{ display: 'block', maxWidth: 96, maxHeight: 96 }} />
                  : <span>{img.objective || (img.magnification ? `${img.magnification}×` : t('image'))}</span>}
              </button>
            ))}
          </div>
        </section>
      )}
      {item && detailOpen && (
        <ScanDetail key={`${item.code}:${item.status}`} item={item} t={t} onClose={closeDetail}
          onSaved={(note) => refresh(note)}>
          {actions.length > 0
            ? actions.map((a) => <ActionForm key={a.action} action={a} item={item} t={t} onDone={refresh} onQueued={queuedNote} />)
            : null}
        </ScanDetail>
      )}
      {viewing && <MicroscopyViewer key={viewing.image_id} image={viewing}
        onChange={(img) => setImages(images.map((i) => (i.image_id === img.image_id ? img : i)))} />}
      <QRScanModal open={scanning} onClose={() => setScanning(false)}
        onDetected={(scanned) => { setScanning(false); resolve(scanned); }} />
    </div>
  );
}
